import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ShiftId } from '@wilson/api/gateway';
import { Shift, TimelineShift } from '@wilson/interfaces';
import { AssignShiftHelperService } from '@wilson/non-domain-specific/shift-helpers/core';
import {
  CheckShiftActivityPermissionsService,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { ShiftTimelineDataTransformerService } from '@wilson/shift-timeline/state';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  SelectedShift,
  ShiftDrawerService,
} from '../../../services/shift-drawer.service';
import { TimelineShiftAssignService } from '../../../services/timeline-shift-assign.service';
import { UnassignShiftCommonOperationsService } from '../../../services/unassign-shift-common-operations.service';
import { MultipleShiftsDrawerComponent } from '../../drawers/multiple-shifts-drawer/multiple-shifts-drawer.component';
import { TimelineDrawerComponent } from '../../drawers/timeline-drawer/timeline-drawer.component';
import { TimelineMultiSelectHelperService } from '../../timeline-multiselect/timeline-multi-select-helper.service';

@Component({
  selector: 'wilson-cell-view-drawers',
  template: `
    @let isDrawerVisible = (isDrawerVisible$ | async) ?? false;
    @let selectedShiftId = (selectedShiftId$ | async) ?? null;
    <wilson-simplified-or-detail-shift-wrapper
      [isDrawerVisible]="isDrawerVisible"
      [selectedShiftId]="selectedShiftId"
      (closeDrawer)="shiftDrawerService.closeDrawer()"
      (deletionConfirmed)="handleShiftDeletion($event)"
    >
      <ng-template>
        <wilson-timeline-drawer
          (deletionConfirmed)="handleShiftDeletion($event)"
          (reloadedUnassignedShift)="handleReloadUnassignedShift($event)"
          (closeDrawer)="shiftDrawerService.closeDrawer()"
        ></wilson-timeline-drawer>
      </ng-template>
    </wilson-simplified-or-detail-shift-wrapper>
    <wilson-multiple-shifts-drawer
      (deleteOperationTriggered)="handleMultiShiftDeletion($event)"
      (unassignShiftsOperationTriggered)="handleMultiShiftUnassign($event)"
      (moveShiftOperationTriggered)="handleMultiShiftMove($event)"
      (assignShiftOperationTriggered)="handleMultiShiftAssignOperation($event)"
    ></wilson-multiple-shifts-drawer>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TimelineMultiSelectHelperService,
    TimelineShiftAssignService,
    AssignShiftHelperService,
  ],
  standalone: false,
})
export class CellViewDrawersComponent implements OnDestroy {
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  protected readonly shiftDrawerService = inject(ShiftDrawerService);
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private timelineMultiSelectHelperService = inject(
    TimelineMultiSelectHelperService,
  );
  private readonly unassignShiftCommonOperationsService = inject(
    UnassignShiftCommonOperationsService,
  );
  private readonly assignShiftHelperService = inject(AssignShiftHelperService);
  private readonly timelineShiftAssignService = inject(
    TimelineShiftAssignService,
  );
  protected shiftTimelineDataTransformerService = inject(
    ShiftTimelineDataTransformerService,
  );
  private readonly checkShiftActivityPermissionsService = inject(
    CheckShiftActivityPermissionsService,
  );

  @ViewChild(MultipleShiftsDrawerComponent)
  multiShiftDrawer!: MultipleShiftsDrawerComponent;

  @ViewChild(TimelineDrawerComponent)
  timelineDrawer!: TimelineDrawerComponent;

  protected readonly isDrawerVisible$ =
    this.shiftDrawerService.isDrawerVisible$;
  protected readonly selectedShiftId$ =
    this.shiftDrawerService.selectedShiftId$;

  async handleMultiShiftUnassign(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      const shiftsToUnassign: (Shift & { id: string })[] = [];
      const shiftsToUnassignIds: ShiftId[] = [];
      for (const shift of shifts) {
        if (shift.userId) {
          shiftsToUnassign.push(shift);
          shiftsToUnassignIds.push(shift.id);
        }
      }

      this.checkShiftActivityPermissionsService.checkShiftActivityPermissions({
        shiftIds: shiftsToUnassignIds,
        onSuccess: async () => {
          await this.unassignShiftCommonOperationsService.handleCellViewUnassign(
            shiftsToUnassign,
          );
        },
      });

      this.multiShiftDrawer.refreshDrawer();
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  async handleShiftDeletion(shift: Shift & { id: string }): Promise<void> {
    try {
      await this.shiftDrawerService.deleteShiftInCellViewMode(shift);
    } catch {
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  async handleMultiShiftDeletion(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      await this.timelineMultiSelectHelperService.handleMultiShiftCellViewDeleteOperation(
        shifts,
      );
      this.multiShiftDrawer.closeDrawer();
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  async handleMultiShiftMove(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    try {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
      await this.timelineMultiSelectHelperService.handleMultiShiftCellViewMoveOperation(
        shifts,
      );
      this.multiShiftDrawer.refreshDrawer();
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  async handleMultiShiftAssignOperation(
    shifts: (Shift & { id: string })[],
  ): Promise<void> {
    this.multiShiftDrawer.isOperationOnGoingSignal.set(true);
    try {
      const result = await this.assignShiftHelperService.assignShifts(
        cloneDeep(shifts),
      );
      if (result) {
        await this.timelineShiftAssignService.handleCellViewShiftsWithoutActivitiesAssignments(
          result,
          shifts.map(
            (shift) =>
              ({
                ...shift,
                isHighlighted: true,
              }) as TimelineShift,
          ),
        );

        this.multiShiftDrawer.refreshDrawer();
      }
    } finally {
      this.multiShiftDrawer.isOperationOnGoingSignal.set(false);
    }
  }

  handleReloadUnassignedShift(shift: SelectedShift): void {
    this.shiftDrawerService.updateCellViewUnassignedShifts(shift);
  }

  ngOnDestroy(): void {
    this.shiftMultiSelectHelperService.clearSelection();
  }
}
